import { WModalButton } from "@/components/button/WToolButton.tsx";
import { t_Create_Button } from "@/components/forms/CreateFormAction.tsx";
import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { createBucketMutation } from "@/modules/storage/bucket/bucketApi.ts";
import { cn } from "@/utils/classNames.ts";
import { useLingui } from "@lingui/react/macro";
import { isValidBucketName } from "@warrenio/api-spec/spec/bucket_name";
import { Input } from "react-aria-components";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";

interface CreateBucketInputs {
    name: string;
    storage_account_uuid: string;
}

export interface CreateBucketModalProps {
    storageAccountUuid: string;
    isDisabled?: boolean;
}

function CreateBucketModalContent({ storageAccountUuid }: CreateBucketModalProps) {
    //#region Hooks
    const { t } = useLingui();
    const createMutation = useStandardMutation(createBucketMutation);
    //#endregion

    const onSubmit: SubmitHandler<CreateBucketInputs> = async (inputs) => {
        const { name } = inputs;

        await createMutation.mutateAsync({
            body: {
                name,
                storage_account_uuid: storageAccountUuid,
            },
        });
    };

    const form = useForm<CreateBucketInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            name: "",
            storage_account_uuid: storageAccountUuid,
        },
    });

    const { control } = form;

    return (
        <WModalForm className="w-sm" form={form} onSubmit={onSubmit} title={t`Create new`} label={t_Create_Button}>
            <Controller
                control={control}
                name="name"
                rules={{
                    required: t(requiredMessage),
                    validate: (value) => (isValidBucketName(value) ? true : t`Please enter a valid name`),
                }}
                render={(props) => (
                    <WTextField
                        {...formPropsToAriaProps(props)}
                        autoFocus
                        isRequired
                        headless
                        block
                        label={t`Storage bucket name`}
                        description={t`The name can only contain lowercase alphanumeric characters and dashes, it must start with a lowercase letter or number, the allowed length is 3–63.`}
                    >
                        <Input className={cn(FF.FormFieldInput, TF.Input)} />
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}

export function CreateBucketModal({ storageAccountUuid, isDisabled }: CreateBucketModalProps) {
    const { t } = useLingui();
    return (
        <WModal
            button={<WModalButton variant="border" label={t`Create new`} isDisabled={isDisabled} icon="jp-icon-add" />}
        >
            <CreateBucketModalContent storageAccountUuid={storageAccountUuid} />
        </WModal>
    );
}
